// Norwegian (Bokmål, du) copy for /on-demand-ftp, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// "FTP ved behov"; "nettopplasteren" for the web uploader.
// The uploader's and FileZilla's own labels are in English, so «labels» are quoted as the app shows them.
import type { OnDemandFtpCopy } from "./copy.en";

const no: OnDemandFtpCopy = {
  seo: {
    title: "Slik fungerer FTP ved behov | Photohawk",
    description:
      "Hvis FTP er helt nødvendig, kan alle i arbeidsområdet ditt starte sin egen Photohawk-FTP-server fra nettopplasteren, på alle abonnementer. Klar på omtrent 30 sekunder, på en personlig adresse som aldri endres, og den slår seg av selv etter en time uten aktivitet.",
  },
  hero: {
    badge: "Alle abonnementer",
    eyebrow: "Tilgjengelig nå",
    title: "Slik fungerer FTP ved behov",
    subtitle:
      "Hvis FTP er helt nødvendig, starter du din egen FTP-server fra nettopplasteren når du trenger den. Den er klar på omtrent 30 sekunder og slår seg av selv når du er ferdig.",
  },
  before: {
    title: "Før du begynner: nettopplasteren er raskere",
    body: "Nettopplasteren er standardmåten å laste opp på, på alle abonnementer.",
    points: [
      { id: "parallel", text: "Den sender mange bilder samtidig, langt flere enn et FTP-program som FileZilla (2 om gangen som standard, og aldri mer enn 10)." },
      { id: "adaptive", text: "Den tilpasser seg forbindelsen din for å utnytte den fullt ut." },
      { id: "live", text: "Forhåndsvisninger går først, så galleriet ditt er på nett på minutter mens originalene kommer etter." },
      { id: "resume", text: "Hvis forbindelsen brytes, fortsetter den av seg selv." },
    ],
    button: "Åpne nettopplasteren",
    link: "Se den nye nettopplasteren",
  },
  what: {
    heading: "Din egen FTP-server, når du trenger en",
    body: "Alle i arbeidsområdet ditt kan starte sin egen FTP-server fra nettopplasteren, på alle abonnementer. Hver person får sin egen server, på en personlig adresse som aldri endres, så du setter opp FTP-programmet ditt bare én gang. Ingenting kjører når du ikke bruker den: den slår seg av selv etter en time uten aktivitet.",
    date: "1. november 2026",
    closing: "De delte FTP-serverne våre, som alltid er på, stenges 1. november 2026. Bruker du dem i dag? Start din egen server og endre verten i FTP-programmet ditt.",
  },
  steps: {
    heading: "Slik fungerer det",
    subtitle: "Fire steg første gang. Etter det starter du serveren og laster opp.",
    items: [
      { id: "open", title: "Åpne nettopplasteren", body: "Gå til `upload.photohawk.com` og logg inn, eller åpne fanen «Upload» i et galleri." },
      { id: "start", title: "Start serveren din", body: "Klikk «FTP server» øverst, og deretter «Start FTP server». Den er klar på omtrent 30 sekunder." },
      { id: "connect", title: "Kopier adressen og påloggingen inn i FTP-programmet ditt", body: "Adressen din, port 21, e-postadressen og passordet ditt hos Photohawk. Adressen endres aldri." },
      { id: "upload", title: "Last opp", body: "Legg bildene dine i mappen til galleriet. Serveren slår seg av etter en time uten opplasting." },
    ],
    button: "Start FTP-serveren din",
    buttonNote: "Åpner nettopplasteren med FTP-serveren din klar til å startes.",
  },
  panel: {
    heading: "Dette ser du",
    body: "Alt finner du i panelet «Your FTP server» i nettopplasteren, med en kopieringsknapp ved hver opplysning.",
    points: [
      { id: "address", text: "Adressen din, for eksempel `swift-heron-4821.ftp.photohawk.com`. Den er din for godt, så du setter den opp bare én gang." },
      { id: "ip", text: "Serverens IP-adresse vises også. Den endres hver gang du starter serveren, så bruk adressen når du kan." },
      { id: "countdown", text: "En nedtelling til den slår seg av, med «Keep it running» og «Stop»." },
    ],
    alt: "Panelet «Your FTP server» som viser Ready i Europa (Irland), med verten swift-heron-4821.ftp.photohawk.com, IP-adressen, port 21, brukernavnet alex@example.com, passiv modus og FTP over TLS, og en nedtelling med knappene Keep it running og Stop.",
  },
  details: {
    heading: "Tilkoblingsdetaljene dine",
    subtitle: "Skriv inn disse i FTP-programmet ditt.",
    rows: [
      { id: "host", label: "Vert", value: "Adressen din, f.eks. `swift-heron-4821.ftp.photohawk.com` (eller IP-adressen som vises)" },
      { id: "port", label: "Port", value: "21" },
      { id: "user", label: "Brukernavn", value: "E-postadressen din hos Photohawk" },
      { id: "password", label: "Passord", value: "Photohawk-passordet ditt, eller FTP-passordet ditt (se nedenfor)" },
      { id: "mode", label: "Overføringsmodus", value: "Passiv" },
      { id: "encryption", label: "Kryptering", value: "Vanlig FTP, eller FTPS (eksplisitt FTP over TLS). Bruk vertsnavnet for FTPS, ikke IP-adressen." },
    ],
    switching: "Har du brukt Photohawk-FTP før? Behold brukernavnet, passordet og mappene dine. Bare endre verten til den nye adressen din.",
  },
  password: {
    heading: "Et eget FTP-passord, hvis du vil",
    body: "I stedet for å lagre Photohawk-passordet ditt i et FTP-program kan du klikke «Create FTP password» i panelet. Det fungerer bare på FTP-serveren din, med e-postadressen din som brukernavn. Du kan kopiere det, lage et nytt (det gamle slutter å virke innen ett minutt) eller fjerne det når som helst. Photohawk-passordet ditt fungerer fortsatt også.",
  },
  folders: {
    heading: "Mappene dine",
    body: "Du ser de samme mappene som før:",
    path: "/Galleries/<gallery>/<collection>",
    points: [
      { id: "single", text: "Et galleri med én samling er rett og slett `/Galleries/<gallery>`." },
      { id: "workspaces", text: "Laster du opp til mer enn ett arbeidsområde? Hvert av dem har sin egen mappe, med galleriene inni." },
      { id: "create", text: "Opprett galleriet og samlingene i Photohawk først. Bildene dine dukker opp i galleriet i løpet av et øyeblikk, akkurat som med nettopplasteren." },
    ],
  },
  off: {
    heading: "Den slår seg av selv",
    body: "Serveren din slår seg av etter 1 time uten aktivitet, så du trenger ikke å huske noe. Panelet viser en nedtelling.",
    points: [
      { id: "keep", text: "«Keep it running» starter nedtellingen på nytt uten at du laster opp noe." },
      { id: "stop", text: "«Stop» slår den av med en gang når du er ferdig." },
      { id: "caps", text: "For sikkerhets skyld slår den seg også av etter 12 timer uten opplasting, og senest etter 24 timer." },
      { id: "again", text: "Start den igjen når du vil, med samme adresse og pålogging." },
    ],
  },
  regions: {
    heading: "Regioner",
    body: "Kjør serveren din i Europa (Irland), USA øst (N. Virginia) eller Asia–Stillehavet (Malaysia). Den nærmeste regionen velges for deg, og du kan endre den før du starter.",
  },
  filezilla: {
    heading: "Eksempel: slik setter du opp FileZilla",
    subtitle: "Andre FTP-programmer bruker de samme innstillingene, noen ganger med litt andre navn.",
    steps: [
      { id: "site", text: "Åpne «File → Site Manager» og klikk «New site»." },
      { id: "protocol", text: "Protokoll: «FTP – File Transfer Protocol»." },
      { id: "host", text: "Vert: adressen din, f.eks. `swift-heron-4821.ftp.photohawk.com`. Port: `21`." },
      { id: "tls", text: "Kryptering: «Use explicit FTP over TLS if available»." },
      { id: "login", text: "Påloggingstype: «Normal». Bruker: e-postadressen din hos Photohawk. Passord: FTP-passordet ditt eller Photohawk-passordet ditt." },
      { id: "passive", text: "Velg «Passive» i fanen «Transfer Settings». Klikk «Connect», og åpne deretter `Galleries`." },
    ],
    tip: "Skal du sende en stor mappe? FileZilla sender 2 filer om gangen som standard, og aldri mer enn 10. Du kan øke innstillingen for samtidige overføringer til 10 («Settings → Transfers»), eller bruke nettopplasteren, som sender mange flere samtidig.",
  },
  trouble: {
    heading: "Hvis noe ikke fungerer",
    items: [
      { id: "connect", title: "Får ikke koblet til", body: "Sjekk at panelet viser «Ready». Hvis vertsnavnet nettopp ble opprettet, bruk IP-adressen i ett minutt." },
      { id: "listing", title: "Tilkoblet, men mappelisten henger", body: "Slå på passiv modus." },
      { id: "logins", title: "“Too many failed logins”", body: "Vent 15 minutter, og sjekk deretter brukernavnet (e-postadressen din) og passordet." },
    ],
  },
  faq: {
    heading: "Spørsmål",
    items: [
      {
        q: "Hvorfor slo serveren min seg av?",
        a: "Den slår seg av etter en time uten aktivitet, så den aldri kjører når du ikke bruker den. Start den igjen når som helst: adressen og påloggingen er de samme. For å holde den i gang uten å laste opp, klikk «Keep it running». Den slår seg også av etter 12 timer uten opplasting, og senest etter 24 timer.",
      },
      {
        q: "Kan to personer kjøre servere samtidig?",
        a: "Ja. Alle i arbeidsområdet ditt kan starte sin egen server, én hver, på sin egen adresse. Bare du kan logge inn på din.",
      },
      {
        q: "Er det sikkert?",
        a: "Ja. Med FTPS (FTP over TLS) på vertsnavnet ditt er forbindelsen kryptert med et klarert sertifikat. Bare påloggingen din fungerer på serveren din, og etter gjentatte mislykkede pålogginger stenger serveren den forbindelsen ute i 15 minutter.",
      },
      {
        q: "Koster det ekstra?",
        a: "Nei. Din egen FTP-server er inkludert i alle abonnementer.",
      },
      {
        q: "Er det med på alle abonnementer?",
        a: "Ja, også på Lite. Til vanlig opplasting er nettopplasteren og Photohawk Studio raskere; din egen FTP-server er der når FTP er helt nødvendig.",
      },
      {
        q: "Hva skjer med den gamle delte FTP-en?",
        a: "De delte FTP-serverne som alltid er på, stenges 1. november 2026, og fungerer som før frem til da. Vil du fortsette å bruke FTP, starter du din egen server og endrer verten i FTP-programmet ditt til den nye adressen. Brukernavnet, passordet og mappene dine forblir de samme.",
      },
    ],
  },
  cta: {
    heading: "Klar når du er det",
    body: "Til vanlige opplastinger bruker du nettopplasteren. Når FTP er helt nødvendig, er din egen server bare et klikk unna.",
    primary: "Åpne nettopplasteren",
    secondary: "Start FTP-serveren din",
  },
};

export default no;
